import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getRefCode } from "@/lib/refTracking";
import Seo from "@/components/Seo";
import Countdown, { useLaunchState } from "@/components/Countdown";
import FoundingBadge from "@/components/FoundingBadge";
import { Flame, Lock, Sparkles, ArrowRight, Minus, Plus, ShoppingBag, BellRing, Check, Heart, ChefHat, Fingerprint } from "lucide-react";
import { MarkdownPreview } from "@/components/MarkdownEditor";
import ImageCarousel from "@/components/ImageCarousel";
import ShippingEstimateWidget from "@/components/ShippingEstimateWidget";
import ShipmentPreview from "@/components/ShipmentPreview";
import CookAlongVideo from "@/components/CookAlongVideo";

function StatusChip({ status }) {
  const map = {
    upcoming: { label: "Drops soon", cls: "bg-[var(--ff-primary)] text-white border-transparent" },
    live: { label: "Live now", cls: "bg-[var(--ff-primary)] text-white border-transparent" },
    almost_sold_out: { label: "Almost gone", cls: "bg-[var(--ff-citrus)] text-[var(--ff-text-primary)] border-transparent" },
    ended: { label: "Ended", cls: "bg-[var(--ff-border)] text-[var(--ff-text-muted)] border-transparent" },
    sold_out: { label: "Sold out", cls: "bg-red-900 text-white border-transparent" },
  };
  const m = map[status] || map.upcoming;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border rounded-full ${m.cls}`}
          data-testid={`drop-status-${status}`}>
      {status === "live" && <Flame size={11} />}
      {m.label}
    </span>
  );
}

function FavoriteButton({ slug, favorited, onToggle, size = 18, className = "" }) {
  const [busy, setBusy] = useState(false);
  const click = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/drops/${slug}/favorite`);
      onToggle?.(slug, data.favorited);
    } catch {
      // Silently fail — non-blocking enhancement.
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} aria-label={favorited ? "Remove from saved" : "Save for later"} data-testid={`drop-fav-${slug}`}
      className={`p-2 rounded-full bg-white/95 backdrop-blur shadow-sm transition-colors ${favorited ? "text-rose-600" : "text-stone-500 hover:text-rose-600"} ${className}`}>
      <Heart size={size} fill={favorited ? "currentColor" : "none"} strokeWidth={2}/>
    </button>
  );
}

function DropCard({ d, isMember, favorited, onToggleFav, speed }) {
  return (
    <Link to={`/drops/${d.slug}`} className="card-flat overflow-hidden group block relative" data-testid={`drop-card-${d.slug}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--ff-muted)]">
        {d.image_url && (
          <img src={d.image_url} alt={d.name} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        )}
        <div className="absolute top-4 left-4"><StatusChip status={d.status}/></div>
        {isMember && <FavoriteButton slug={d.slug} favorited={favorited} onToggle={onToggleFav} className="absolute top-3 right-3"/>}
        {!isMember && d.total_inventory > 0 && d.status !== "sold_out" && d.status !== "ended" && (
          <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-semibold tabular-nums">
            {d.remaining}/{d.total_inventory} left
          </div>
        )}
      </div>
      <div className="p-6">
        <p className="overline">{d.farm_name || "Curated"}</p>
        <h3 className="font-display text-2xl mt-2 group-hover:text-[var(--ff-primary)] transition-colors">{d.name}</h3>
        <p className="text-sm text-[var(--ff-text-secondary)] mt-2 line-clamp-2">{d.description}</p>
        {speed && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
            data-testid={`drop-speed-${d.slug}`}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/>
            Fastest to you — {speed.warehouseName} → {speed.state} · {speed.days}-day zone
          </div>
        )}
        <div className="mt-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="font-semibold text-lg" data-testid={`drop-price-${d.slug}`}>
              ${d.price.toFixed(0)}
            </span>
            {!isMember && d.non_member_price != null && (
              <p
                className="text-[11px] text-[var(--ff-text-muted)] leading-tight mt-0.5"
                data-testid={`drop-non-member-price-${d.slug}`}
              >
                Non-members pay ${Number(d.non_member_price).toFixed(2)}
              </p>
            )}
          </div>
          {d.status === "upcoming" && (
            <span className="text-xs text-[var(--ff-text-muted)] tabular-nums shrink-0">
              <Countdown to={d.drops_at} compact data-testid={`drop-cd-${d.slug}`}/>
            </span>
          )}
          {d.status === "live" && d.ends_at && (
            <span className="text-xs text-[var(--ff-text-muted)] tabular-nums shrink-0">
              ends in <Countdown to={d.ends_at} compact />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function DropsNotifyForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | err
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    try {
      await api.post("/waitlist/join", { email: email.trim(), source: "drops_launch" });
      setState("done");
    } catch {
      setState("err");
    }
  };
  if (state === "done") return (
    <div className="mt-8 max-w-md mx-auto bg-[var(--ff-primary)] text-white rounded-2xl px-6 py-5" data-testid="drops-notify-done">
      <p className="font-display text-xl">You&apos;re on the list! 🎉</p>
      <p className="text-sm text-white/85 mt-1">We&apos;ll email you the moment the first drop goes live on Black Friday, November 27.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="mt-8 max-w-md mx-auto" data-testid="drops-notify-form">
      <p className="text-sm font-medium text-[var(--ff-text-secondary)] mb-2">Get pinged the instant the first drop goes live:</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder="you@email.com" className="input-base flex-1" data-testid="drops-notify-email"
        />
        <button type="submit" disabled={state === "busy"} className="btn-primary whitespace-nowrap justify-center" data-testid="drops-notify-submit">
          {state === "busy" ? "Joining…" : "Notify me"}
        </button>
      </div>
      {state === "err" && <p className="text-xs text-red-700 mt-2" data-testid="drops-notify-err">Something went wrong — please try again.</p>}
    </form>
  );
}

export function Drops() {
  const [drops, setDrops] = useState(null);
  const [favSet, setFavSet] = useState(new Set());
  const [speed, setSpeed] = useState(null);  // {warehouseName, state, days}
  const launch = useLaunchState();
  const { user } = useAuth();
  const isMember = !!user?.is_member;

  useEffect(() => { api.get("/drops").then(r => setDrops(r.data)).catch(() => setDrops([])); }, []);
  useEffect(() => {
    if (!isMember) return;
    api.get("/drops/me/favorite-slugs").then(r => setFavSet(new Set(r.data.slugs || []))).catch(() => {});
  }, [isMember]);

  // Delivery-speed badge — hydrate from the last ZIP the customer entered on
  // the drop page. One API call, badge appears across all cards.
  useEffect(() => {
    const zip = localStorage.getItem("ff2d_est_zip");
    if (!zip || zip.length < 5) return;
    api.post("/shipping/estimate-by-zip", { zip })
      .then(({ data }) => {
        const wh = data?.warehouse;
        const st = data?.place?.state;
        const days = wh?.transit_days ?? data?.cheapest?.estimated_days;
        if (wh?.name && st && days) {
          setSpeed({
            warehouseName: wh.name.replace(" Fulfillment Center", ""),
            state: st,
            days,
          });
        }
      })
      .catch(() => {});
  }, []);

  const toggleFav = (slug, isNowFav) => {
    setFavSet(prev => {
      const next = new Set(prev);
      if (isNowFav) next.add(slug); else next.delete(slug);
      return next;
    });
  };

  return (
    <div data-testid="drops-page">
      <Seo title="Exclusive Drops" description="Members-only limited releases of premium American-raised beef and chicken from independent family ranches." path="/drops"/>
      <section className="relative text-white overflow-hidden" data-testid="drops-hero">
        <img
          src="/assets/angus-box.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          aria-hidden
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,67,49,0.88) 0%, rgba(26,67,49,0.72) 55%, rgba(0,0,0,0.78) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16">
          <p className="overline text-[var(--ff-citrus)]">Members-only · Drop calendar</p>
          <h1 className="font-display text-5xl sm:text-7xl mt-3 leading-[1.02] max-w-3xl">
            Curated drops.<br/>Real farms.<br/>Members only.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/85 leading-relaxed">
            Curated releases of premium beef and chicken from American family ranches — limited quantities, hand-packed the morning of shipping. <strong>Membership is the only way in.</strong>
          </p>
          {launch?.launch_at && launch.phase !== "launched" && (
            <div className="mt-10 inline-flex flex-col bg-white/10 backdrop-blur rounded-2xl p-6">
              <p className="overline text-[var(--ff-citrus)]">Drops officially launch</p>
              <div className="mt-2 [&_.overline]:text-white/70 [&_span]:text-white">
                <Countdown to={launch.launch_at} data-testid="drops-hero-countdown"/>
              </div>
            </div>
          )}
        </div>
      </section>

      {!isMember && (
        <section className="bg-[var(--ff-citrus)] text-[var(--ff-text-primary)]" data-testid="drops-gate-banner">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock size={18}/>
              <p className="text-sm sm:text-base"><strong>Drops are members-only.</strong> Browse the calendar below — purchasing requires an active membership.</p>
            </div>
            <Link to="/pricing" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="drops-gate-cta">
              <Sparkles size={16}/> Become a member
            </Link>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline">All drops</p>
            <h2 className="font-display text-3xl mt-2">Upcoming releases</h2>
          </div>
          <Link to="/pricing" className="hidden md:inline-flex btn-primary items-center gap-2">
            <Sparkles size={16}/> Become a member
          </Link>
        </div>

        {drops === null && <p className="text-[var(--ff-text-muted)]">Loading drops…</p>}
        {drops !== null && (
          <div className="rounded-3xl border-2 border-dashed border-[var(--ff-border)] bg-[var(--ff-muted)]/40 p-12 sm:p-20 text-center" data-testid="drops-coming-soon">
            <div className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"/>
              First drops coming soon
            </div>
            <h3 className="font-display text-3xl sm:text-5xl mt-6 max-w-2xl mx-auto leading-tight">
              Our first drops launch November 27.
            </h3>
            <p className="mt-5 text-[var(--ff-text-secondary)] max-w-xl mx-auto leading-relaxed">
              We&apos;re lining up our founding ranchers now. Become a member today to be first in line the moment the drops go live.
            </p>
            <DropsNotifyForm />
            <Link to="/pricing" className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="drops-coming-soon-cta">
              Lock in your founding seat <ArrowRight size={16}/>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------- Drop detail with full interactive flow ----------

function ReserveModal({ drop, onClose, onCheckout }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const honeypotRef = useRef("");

  const reserveAndCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const reserveRes = await api.post(`/drops/${drop.slug}/reserve`, { quantity: qty, honeypot: honeypotRef.current });
      const refCode = getRefCode();
      const co = await api.post(`/drops/${drop.slug}/checkout`, {
        reservation_id: reserveRes.data.id,
        origin_url: window.location.origin,
        honeypot: honeypotRef.current,
        ...(refCode ? { ref_code: refCode } : {}),
      });
      if (co.data?.url) window.location.href = co.data.url;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Reservation failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="reserve-modal">
      <div className="bg-[var(--ff-bg)] rounded-2xl max-w-md w-full p-8">
        <p className="overline">Reserve your spot</p>
        <h3 className="font-display text-2xl mt-2">{drop.name}</h3>
        <p className="text-sm text-[var(--ff-text-secondary)] mt-1">${drop.price.toFixed(2)} each · {drop.remaining} left</p>

        <input type="text" name="website" defaultValue="" tabIndex={-1} aria-hidden
               onChange={e => { honeypotRef.current = e.target.value; }}
               className="absolute -left-[9999px] w-px h-px opacity-0" autoComplete="off"/>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-sm text-[var(--ff-text-secondary)]">Quantity</span>
          <div className="inline-flex items-center bg-[var(--ff-muted)] rounded-full" data-testid="reserve-qty-control">
            <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-2 hover:text-[var(--ff-primary)]" data-testid="reserve-qty-minus"><Minus size={14}/></button>
            <span className="px-3 tabular-nums font-semibold" data-testid="reserve-qty-value">{qty}</span>
            <button onClick={() => setQty(q => Math.min(10, drop.remaining, q + 1))} className="p-2 hover:text-[var(--ff-primary)]" data-testid="reserve-qty-plus"><Plus size={14}/></button>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-[var(--ff-muted)] text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-[var(--ff-text-secondary)]">Subtotal</span><span className="tabular-nums">${(drop.price * qty).toFixed(2)}</span></div>
          <div className="flex justify-between">
            <span className="text-[var(--ff-text-secondary)]">Cold-chain shipping</span>
            {drop.free_shipping_credits > 0
              ? <span className="text-[var(--ff-primary)] font-semibold">Free (founding perk)</span>
              : <span className="tabular-nums">$9.00</span>}
          </div>
        </div>

        {err && <p className="mt-4 text-sm text-red-700" data-testid="reserve-err">{err}</p>}

        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancel</button>
          <button onClick={reserveAndCheckout} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="reserve-confirm-btn">
            <ShoppingBag size={16}/> {busy ? "Reserving…" : "Reserve & checkout"}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-[var(--ff-text-muted)] text-center">Reservation holds your spot for 10 minutes.</p>
      </div>
    </div>
  );
}

function WaitlistButton({ slug, status, "data-testid": testId = "waitlist-btn" }) {
  const [onList, setOnList] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/drops/${slug}/waitlist/me`).then(r => setOnList(r.data.on_waitlist)).catch(() => setOnList(false));
  }, [slug]);

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/drops/${slug}/waitlist`, { honeypot: "" });
      setOnList(true);
    } finally { setBusy(false); }
  };

  if (onList === null) return null;
  if (onList) return (
    <div className="btn-secondary w-full justify-center inline-flex items-center gap-2 cursor-default" data-testid={`${testId}-joined`}>
      <Check size={16}/> You&apos;re on the waitlist
    </div>
  );
  return (
    <button onClick={join} disabled={busy} className="btn-secondary w-full justify-center inline-flex items-center gap-2" data-testid={testId}>
      <BellRing size={16}/> {busy ? "Joining…" : `Notify me when ${status === "sold_out" ? "restocked" : "live"}`}
    </button>
  );
}

export function DropDetail() {
  const slug = window.location.pathname.split("/drops/")[1];
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [inv, setInv] = useState(null);
  const [showReserve, setShowReserve] = useState(false);
  const [ship, setShip] = useState(null);  // {zip, state, warehouse_id} — resolved by the estimate widget

  // Initial load
  useEffect(() => {
    api.get(`/drops/${slug}`)
      .then(r => { setD(r.data); setInv({ remaining: r.data.remaining, sold: r.data.sold, status: r.data.status, pct_sold: r.data.pct_sold }); })
      .catch(e => setErr(e.response?.data?.detail || "Not found"));
  }, [slug]);

  // Live inventory — WebSocket with HTTP-polling fallback.
  useEffect(() => {
    if (!slug) return;
    let ws = null;
    let pollId = null;
    let cancelled = false;

    const startPolling = () => {
      if (pollId) return;
      pollId = setInterval(() => {
        api.get(`/drops/${slug}/inventory`)
          .then(r => !cancelled && setInv(r.data))
          .catch(() => {});
      }, 5000);
    };

    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/, "ws");
      ws = new WebSocket(`${wsBase}/api/ws/drops/${slug}/inventory`);
      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(ev.data);
          if (!data.error) setInv(data);
        } catch { /* noop */ }
      };
      ws.onerror = () => { startPolling(); };
      ws.onclose = () => {
        if (!cancelled && !pollId) startPolling();
      };
    } catch {
      startPolling();
    }

    return () => {
      cancelled = true;
      if (ws) { try { ws.close(); } catch { /* noop */ } }
      if (pollId) clearInterval(pollId);
    };
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="drop-not-found">
    <h1 className="font-display text-4xl">{err}</h1>
    <Link to="/drops" className="btn-secondary mt-6 inline-flex">Back to drops</Link>
  </div>;
  if (!d) return <div className="p-12 text-center text-[var(--ff-text-muted)]" data-testid="drop-loading">Loading…</div>;

  // Merge live inventory data over the initial snapshot
  const status = inv?.status || d.status;
  const remaining = inv?.remaining ?? d.remaining;
  const sold = inv?.sold ?? d.sold;
  const pct = inv?.pct_sold ?? d.pct_sold;
  const access = d.access || { can_purchase: false, reason: "Sign in to purchase" };
  const dropWithLiveInv = { ...d, remaining, sold, status, pct_sold: pct };

  const countdownTarget = status === "upcoming" ? d.drops_at : status === "live" ? d.ends_at : null;
  const countdownLabel = status === "upcoming" ? "Drops in" : "Ends in";
  const isSoldOut = status === "sold_out" || remaining <= 0;
  const canPurchase = access.can_purchase && !isSoldOut;

  let ctaNode;
  const showNonMemberPrice = !user?.is_member && d.non_member_price != null;
  const buyPrice = user?.is_member ? d.price : (d.non_member_price ?? d.price);
  // Pre-launch lockout: drops open Oct 31. Everything is a "coming soon"
  // teaser until then — no purchasing, no reservations.
  ctaNode = (
    <div className="card-flat p-5 text-center" data-testid="drop-cta-coming-soon">
      <span className="inline-flex items-center gap-1.5 bg-[var(--ff-primary)] text-white text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">Coming soon</span>
      <p className="mt-3 font-display text-xl text-[var(--ff-primary)]">Drops open November 27</p>
      <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Become a member now to unlock this drop the moment it goes live.</p>
      <Link to="/pricing" className="btn-primary w-full justify-center mt-4" data-testid="drop-cta-join">Become a member</Link>
    </div>
  );

  return (
    <div data-testid={`drop-detail-${d.slug}`}>
      <Seo title={d.name} description={d.description} path={`/drops/${d.slug}`} image={d.image_url}/>

      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {d.image_url && <img src={d.image_url} alt={d.name} fetchPriority="high" className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 hero-overlay"/>
        <div className="relative max-w-7xl mx-auto px-6 pb-16 text-white w-full">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <StatusChip status={status}/>
            {d.vendor_id && (
              <Link
                to={`/farms/${d.vendor_id}`}
                className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur border border-white/25 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider hover:bg-white/25 transition-colors"
                data-testid="drop-farm-id-chip"
              >
                <Fingerprint size={11}/> Farm ID · {String(d.vendor_id).slice(0, 8).toUpperCase()}
              </Link>
            )}
            {d.user_founding && <FoundingBadge size="xs"/>}
          </div>
          <h1 className="font-display text-5xl sm:text-7xl max-w-3xl leading-[1.02]">{d.name}</h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85 leading-relaxed">{d.description}</p>
        </div>
      </section>

      {/* GALLERY — full-width above the story fold, feels premium */}
      {Array.isArray(d.images) && d.images.length > 0 && (
        <section className="bg-[var(--ff-muted)] py-14" data-testid="drop-gallery">
          <div className="max-w-7xl mx-auto px-6">
            <p className="overline text-[var(--ff-primary)]">Inside the drop</p>
            <div className="mt-4">
              <ImageCarousel images={d.images} alt={d.name} testId="drop-carousel"/>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <p className="overline">The story</p>
          <h2 className="font-display text-3xl mt-2">Coming soon</h2>
          <div className="mt-6 text-lg" data-testid="drop-story-md">
            <MarkdownPreview value={d.story}/>
          </div>

          {/* Cooking instructions — chef card */}
          {d.cooking_instructions && (
            <div className="mt-10 card-flat p-6 sm:p-8 border-2 border-[var(--ff-accent-kraft)] bg-[var(--ff-accent-kraft)]/10" data-testid="drop-cooking-instructions">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--ff-primary)] text-white">
                  <ChefHat size={20} />
                </span>
                <div>
                  <p className="overline text-[var(--ff-primary)]">From our chef</p>
                  <h3 className="font-display text-2xl text-[var(--ff-primary)]">How to cook it</h3>
                </div>
              </div>
              <div className="mt-5 text-[15px] leading-relaxed">
                <MarkdownPreview value={d.cooking_instructions}/>
              </div>
            </div>
          )}

          <div className="mt-10 card-flat p-6" data-testid="drop-tier-access">
            <p className="overline">Access &amp; pricing</p>
            <ul className="mt-3 text-sm space-y-2">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[var(--ff-citrus)]"></span>
                <strong className="font-semibold">Members</strong>
                <span className="text-[var(--ff-text-muted)]">— full access the moment the drop goes live · member pricing · free shipping</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[var(--ff-border)]"></span>
                <span className="text-[var(--ff-text-muted)]">Non-members — +15% markup · shipping charged separately</span>
              </li>
            </ul>
          </div>
        </div>

        <aside className="space-y-6">
          {countdownTarget && (
            <div className="card-flat p-6 text-center" data-testid="drop-countdown-card">
              <Countdown to={countdownTarget} label={countdownLabel}/>
            </div>
          )}

          <div className="card-flat p-6" data-testid="drop-inventory-live">
            <p className="overline">Live inventory</p>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="font-display text-3xl tabular-nums" data-testid="drop-inv-remaining">{remaining}</span>
              <span className="text-xs text-[var(--ff-text-muted)] tabular-nums">of {d.total_inventory}</span>
            </div>
            <div className="mt-3 h-2 bg-[var(--ff-muted)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--ff-primary)] transition-all duration-1000" style={{width: `${pct}%`}}/>
            </div>
            <p className="text-[11px] text-[var(--ff-text-muted)] mt-2">
              {sold} sold · {pct}% gone
              {remaining > 0 && remaining <= 10 && <span className="text-red-700 ml-2 font-semibold">Only {remaining} left!</span>}
            </p>
          </div>

          <div>{ctaNode}</div>

          <ShippingEstimateWidget isMember={!!user?.is_member} onResolved={setShip} />
          {ship?.state && <ShipmentPreview slug={slug} state={ship.state} zip={ship.zip} quantity={1}/>}

          {showNonMemberPrice && (
            <div className="card-flat p-4" data-testid="drop-non-member-price-card">
              <p className="overline text-[var(--ff-citrus)]">Pricing</p>
              <div className="mt-2 space-y-1.5 text-sm">
                <div className="flex items-baseline justify-between">
                  <span className="text-[var(--ff-text-secondary)]">Members</span>
                  <span className="font-semibold tabular-nums">${Number(d.price).toFixed(2)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[var(--ff-text-secondary)]">Non-members</span>
                  <span className="font-semibold tabular-nums text-[var(--ff-citrus)]">
                    ${Number(d.non_member_price).toFixed(2)}
                  </span>
                </div>
              </div>
              <Link
                to="/pricing"
                className="mt-3 inline-block text-xs font-semibold text-[var(--ff-primary)] hover:underline"
              >
                Save 15% + free shipping — become a member →
              </Link>
            </div>
          )}

          {user && d.user_founding && (
            <div className="card-flat p-4 text-center" data-testid="drop-founding-callout">
              <FoundingBadge/>
              <p className="text-xs text-[var(--ff-text-muted)] mt-2">
                {d.free_shipping_credits > 0
                  ? "You've got 1 free-shipping credit ready to apply at checkout."
                  : "Free-shipping credit already used — thanks for being a founder."}
              </p>
            </div>
          )}
        </aside>
      </section>

      {showReserve && <ReserveModal drop={dropWithLiveInv} onClose={() => setShowReserve(false)}/>}
    </div>
  );
}
